import React, { useEffect } from "react";
import { Card, CardBody, Row, Col, FormGroup, Label, Input, Table } from "reactstrap";
import Switch from "react-switch";

// Stronger than the global $card-box-shadow (0 0 20px rgba(8,21,66,.05)) so the
// three sync sections read as separate panels.
const sectionCardStyle = { boxShadow: "0 4px 18px rgba(8, 21, 66, 0.13)" };

const SellerShopifySyncSettings = (props) => {
  const {
    register,
    getValues,
    setValue,
    trigger,
    errors,
    shopifyLocations,

    // pass these from parent/backend detection (can be [] / false)
    ukCatalogs = [],                 // [{ id, name }]
    gbpContextualAvailable = false,  // true/false
    gbpContextualError = "",         // string (optional)
    watch: watchProp,
  } = props;
  const watch = typeof watchProp === "function" ? watchProp : getValues; // fallback
  const syncInventory = !!watch("syncInventory");
  const selectedIds = (watch("inventoryLocationIds") || []).map(String);

  const syncPrices = !!watch("syncPrices");
  const priceSyncMode = watch("ukPriceSyncMode") || "";
  const selectedCatalogId = watch("ukCatalogId") || "";
  const fallbackToContextual = !!watch("ukCatalogFallbackToContextual");
  const stockBufferValue = watch("stockBufferValue");

  const pushOrdersToShopify = watch("pushOrdersToShopify") !== false;

  useEffect(() => {
    if (!Array.isArray(shopifyLocations) || shopifyLocations.length === 0) return;

    const preselected = shopifyLocations
      .filter((l) => l?.isActive && l?.is_selected)
      .map((l) => String(l.id));

    const current = (getValues("inventoryLocationIds") || []).map(String);

    if (current.length === 0 && preselected.length > 0) {
      setValue("inventoryLocationIds", preselected, { shouldDirty: false, shouldValidate: true });
      trigger(["inventoryLocationIds"]);
    }
  }, [shopifyLocations]);

  const onToggleSyncInventory = (checked) => {
    setValue("syncInventory", checked, { shouldDirty: true, shouldValidate: true });
    trigger(["syncInventory", "inventoryLocationIds"]);
  };

  const onTogglePushOrdersToShopify = (checked) => {
    setValue("pushOrdersToShopify", checked, { shouldDirty: true, shouldValidate: true });
    trigger(["pushOrdersToShopify"]);
  };

  const onToggleSyncPrices = (checked) => {
    setValue("syncPrices", checked, { shouldDirty: true, shouldValidate: true });

    // when turning OFF, reset mode + catalog selection (keep consistent)
    if (!checked) {
      setValue("ukPriceSyncMode", "", { shouldDirty: true, shouldValidate: true });
      setValue("ukCatalogId", null, { shouldDirty: true, shouldValidate: true });
    }

    trigger(["syncPrices", "ukPriceSyncMode", "ukCatalogId", "ukCatalogFallbackToContextual"]);
  };

  const onChangePriceMode = (mode) => {
    setValue("ukPriceSyncMode", mode, { shouldDirty: true, shouldValidate: true });

    // cleanup dependent fields
    if (mode !== "catalog") {
      setValue("ukCatalogId", null, { shouldDirty: true, shouldValidate: true });
    }

    trigger(["ukPriceSyncMode", "ukCatalogId", "ukCatalogFallbackToContextual"]);
  };

  const showCatalogOption = Array.isArray(ukCatalogs) && ukCatalogs.length > 0;
  const showContextualOption = !!gbpContextualAvailable;

  return (
    <>
    <Card className="mt-3" style={sectionCardStyle}>
      <CardBody>
        {/* =========================
            Order Push Settings
        ========================== */}
        <Row>
          <Col md={12}>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div className="font-weight-bold mb-1" style={{ fontSize: 18 }}>Order Push Settings</div>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Send new Goddiva orders to this store's Shopify. Turning this off does not affect inventory or price sync.
                </div>
              </div>

              <div className="d-flex align-items-center">
                <span className="font-weight-bold mr-2">Push orders to Shopify store</span>
                <FormGroup style={{ marginTop: 1, marginLeft: 5, marginBottom: 0 }}>
                  <Switch
                    checked={pushOrdersToShopify}
                    onChange={onTogglePushOrdersToShopify}
                    className="react-switch"
                    uncheckedIcon={false}
                    checkedIcon={false}
                  />
                </FormGroup>

                {/* RHF binding */}
                <input type="hidden" {...register("pushOrdersToShopify")} />
              </div>
            </div>
          </Col>
        </Row>

      </CardBody>
    </Card>

    <Card className="mt-3" style={sectionCardStyle}>
      <CardBody>
        {/* =========================
            Inventory Sync Settings
        ========================== */}
        <Row>
          <Col md={12}>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div className="font-weight-bold mb-1" style={{ fontSize: 18 }}>Inventory Sync Settings</div>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Keep marketplace stock levels in sync with selected Shopify locations
                </div>
              </div>

              <div className="d-flex align-items-center">
                <span className="font-weight-bold mr-2">Sync Inventory</span>
                <FormGroup style={{ marginTop: 1, marginLeft: 5, marginBottom: 0 }}>
                  <Switch
                    checked={syncInventory}
                    onChange={onToggleSyncInventory}
                    className="react-switch"
                    uncheckedIcon={false}
                    checkedIcon={false}
                  />
                </FormGroup>

                {/* RHF binding */}
                <input type="hidden" {...register("syncInventory")} />
              </div>
            </div>
          </Col>
        </Row>

        {/* Stock buffer */}
        <Row className="mt-3">
          <Col md={6}>
            <FormGroup>
              <Label className="form-label font-weight-bold">Stock buffer value</Label>
              <Input
                  type="number"
                  placeholder="e.g. 2"
                  {...register("stockBufferValue", { valueAsNumber: true })}
                  value={stockBufferValue ?? ""}               // controlled by watch
                  onChange={(e) => {
                    setValue("stockBufferValue", e.target.value === "" ? "" : Number(e.target.value), {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    trigger(["stockBufferValue"]);
                  }}
              />
              <small className="text-muted d-block mt-2" style={{ fontSize: 12 }}>
                NOTE: The value specified in the stock buffer field will be deducted from the stock quantity of each
                item imported into Marketplacer.
              </small>
              <div className="text-danger" style={{ fontSize: 12 }}>
                {errors?.stockBufferValue?.message}
              </div>
            </FormGroup>
          </Col>
        </Row>

        {/* Inventory Locations */}
        <Row className="align-items-start">
          <Col md={12}>
            <p className="font-weight-bold mb-1" style={{ fontSize: 16 }}>
              Inventory Locations <span style={{ color: "red" }}>*</span>
            </p>
            <p className="text-muted mb-0" style={{ fontSize: 13 }}>
              Select the locations that contribute to your marketplace availability
            </p>
          </Col>
        </Row>

        <input type="hidden" {...register("inventoryLocationIds")} />

        <div className="mt-3">
          <Table bordered responsive className="mb-2">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Select</th>
                <th>Location Name</th>
                <th style={{ width: 160 }}>Country</th>
              </tr>
            </thead>

            <tbody>
              {(shopifyLocations || []).length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-muted">
                    No Shopify locations found.
                  </td>
                </tr>
              )}

              {(shopifyLocations || []).map((loc) => {
                const id = String(loc.id);
                const checked = selectedIds.includes(id);
                const country = loc?.address?.country || loc?.country || "";

                return (
                  <tr key={loc.id} style={!syncInventory ? { opacity: 0.6 } : undefined}>
                    <td className="text-center align-middle">
                      <Input
                        type="checkbox"
                        checked={checked}
                        disabled={!syncInventory}
                        onChange={() => {
                          const next = checked ? selectedIds.filter((x) => x !== id) : [...selectedIds, id];
                          setValue("inventoryLocationIds", next, { shouldDirty: true, shouldValidate: true });
                          trigger(["inventoryLocationIds"]);
                        }}
                        style={{ position: "relative", transform: "scale(1.25)", top: "2px", cursor: "pointer" }}
                      />
                    </td>

                    <td className="align-middle font-weight-bold">{loc.name}</td>
                    <td className="align-middle">{country || <span className="text-muted">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {syncInventory && (
            <div className="text-danger" style={{ fontSize: 12 }}>
              {errors?.inventoryLocationIds?.message}
            </div>
          )}
        </div>

      </CardBody>
    </Card>

    <Card className="mt-3" style={sectionCardStyle}>
      <CardBody>
        {/* =========================
            UK (GBP) Price Sync
        ========================== */}

        <Row>
          <Col md={12}>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div className="font-weight-bold mb-1" style={{ fontSize: 18 }}>UK (GBP) Price Sync</div>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Choose how UK prices should be synced from the seller Shopify store.
                </div>
              </div>

              <div className="d-flex align-items-center">
                <span className="font-weight-bold mr-2">Sync Prices</span>
                <FormGroup style={{ marginTop: 1, marginLeft: 5, marginBottom: 0 }}>
                  <Switch
                    checked={syncPrices}
                    onChange={onToggleSyncPrices}
                    className="react-switch"
                    uncheckedIcon={false}
                    checkedIcon={false}
                  />
                </FormGroup>

                {/* RHF binding */}
                <input type="hidden" {...register("syncPrices")} />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={12}>
            {/* keep RHF binding */}
            <input type="hidden" {...register("ukPriceSyncMode")} />
            <input type="hidden" {...register("ukCatalogId")} />
            <input type="hidden" {...register("ukCatalogFallbackToContextual")} />

            {!syncPrices && (
              <div className="text-muted" style={{ fontSize: 13 }}>
                Price sync is currently disabled for this seller.
              </div>
            )}

            {syncPrices && (
              <>
                {/* Option 1: Catalog */}
                {showCatalogOption && (
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="ukPriceSyncMode-catalog"
                        name="ukPriceSyncModeRadio"
                        checked={priceSyncMode === "catalog"}
                        onChange={() => onChangePriceMode("catalog")}
                      />
                      <Label className="form-check-label font-weight-bold" for="ukPriceSyncMode-catalog">
                        Use UK Catalog Pricing
                      </Label>
                    </div>

                    {priceSyncMode === "catalog" && (
                      <div className="pl-4 mt-2">
                        <FormGroup>
                          <Label className="form-label font-weight-bold">Select UK Catalog</Label>
                          <Input
                            type="select"
                            value={selectedCatalogId || ""}   // now reflects backend value on load
                            onChange={(e) => {
                              setValue("ukCatalogId", e.target.value || null, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                              trigger(["ukCatalogId"]);
                            }}
                          >
                            <option value="">-- Select catalog --</option>
                            {ukCatalogs.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name} [Currency: {c.currency}]
                              </option>
                            ))}
                          </Input>
                          <div className="text-danger" style={{ fontSize: 12 }}>
                            {errors?.ukCatalogId?.message}
                          </div>
                        </FormGroup>

                        <FormGroup check className="mb-0">
                          <Input
                            type="checkbox"
                            checked={!!getValues("ukCatalogFallbackToContextual")}
                            onChange={(e) => {
                              setValue("ukCatalogFallbackToContextual", e.target.checked, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                              trigger(["ukCatalogFallbackToContextual"]);
                            }}
                            style={{ position: "relative", transform: "scale(1.15)", top: "2px" }}
                          />
                          <Label check className="text-muted ml-2" style={{ fontSize: 13 }}>
                            If price is missing in catalog, fallback to contextual price (country: GB) and report
                            “Missing in catalog – used contextual fallback”.
                          </Label>
                        </FormGroup>
                      </div>
                    )}
                  </div>
                )}

                {/* Option 2: Contextual */}
                {showContextualOption && (
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="ukPriceSyncMode-contextual"
                        name="ukPriceSyncModeRadio"
                        checked={priceSyncMode === "contextual"}
                        onChange={() => onChangePriceMode("contextual")}
                      />
                      <Label className="form-check-label font-weight-bold" for="ukPriceSyncMode-contextual">
                        Use Contextual Pricing (GB)
                      </Label>
                    </div>

                    {priceSyncMode === "contextual" && (
                      <div className="pl-4 mt-2 text-muted" style={{ fontSize: 13 }}>
                        Sync prices using Shopify contextualPricing with context country = GB.
                      </div>
                    )}
                  </div>
                )}

                {/* Option 3: Manual Marketplace (ALWAYS available) */}
                <div className="mb-2">
                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="ukPriceSyncMode-manual"
                      name="ukPriceSyncModeRadio"
                      checked={priceSyncMode === "manual"}
                      onChange={() => onChangePriceMode("manual")}
                    />
                    <Label className="form-check-label font-weight-bold" for="ukPriceSyncMode-manual">
                      Manual Price Update using Marketplace
                    </Label>
                  </div>

                  {priceSyncMode === "manual" && (
                    <div className="pl-4 mt-2 text-muted" style={{ fontSize: 13 }}>
                      UK prices will be managed manually in Marketplace. Shopify UK price sync will not overwrite manual
                      prices.
                    </div>
                  )}
                </div>

                {/* If neither catalog nor GBP contextual available: show hold message (but manual still works) */}
                {!showCatalogOption && !showContextualOption && (
                  <div className="mt-3">
                    <div className="text-danger font-weight-bold" style={{ fontSize: 13 }}>
                      GBP prices not found for auto sync (Catalog/Contextual). Please contact seller. You can still use Manual mode.
                    </div>
                    {!!gbpContextualError && (
                      <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                        {gbpContextualError}
                      </div>
                    )}
                  </div>
                )}

                <div className="text-danger mt-2" style={{ fontSize: 12 }}>
                  {errors?.ukPriceSyncMode?.message}
                </div>
              </>
            )}
          </Col>
        </Row>
      </CardBody>
    </Card>
    </>
  );
};

export default SellerShopifySyncSettings;